import type { AssetDto, ContactDto, FileMarksDto, ReviewFileDto, ReviewSetDto, ReviewerDto } from '@nsb/core';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { ApiError, api, download, errorText, queryClient, upload, useApi } from '../../api.ts';
import { DropZone } from '../../components/DropZone.tsx';
import { useFeedbackRecord } from '../../components/feedback-context.ts';
import { Markup } from '../../components/Markup.tsx';
import { sheetPage } from '../../components/markupWords.ts';
import { RemindAsk, RemindLink } from '../../components/Remind.tsx';
import { useRemind } from '../../components/useRemind.ts';
import { personLine } from '../../components/remindWords.ts';
import { BackLink, Button, Confirm, ErrorLine, Field, LoadError, Loading, Page, Title } from '../../components/ui.tsx';
import { useUnsaved } from '../../components/useUnsaved.ts';
import { sameValue } from '../../unsaved.ts';
import { ProjectCrumb } from './common.tsx';
import { IterationHead } from './IterationHead.tsx';
import { DONE_WORDS, reviewExamples } from './reviewHelp.tsx';
import { assetsKey, useProject, useProjectId } from './hooks.ts';
import { typeOf } from '../../fileTypes.ts';
import {
  askedAgainWords,
  canShare,
  earlierLine,
  heldHint,
  reviewPhases,
  revisedOn,
  reviewStep,
  reviewStepLine,
  roundNotes,
  sendLine,
  sendWords,
  shareNoteLabel,
  sheetGroups,
  type ReviewGroup,
} from './reviewStep.ts';
import { addDrawingSet, ReaderMissing, splitFailed, splitsIntoPlates } from './plates.ts';
import { dateWords } from '../../days.ts';

/**
 * Design reviews (Jason, 2026-10-02): design work shared with the client as
 * iterations under a phase ("Concept", iteration 1). A step of the process
 * that delivers a design review opens its iteration as it is added (T1 B);
 * NSB adds the files and shares it with the people it asks to review it,
 * which puts a review task on them; each marks the files up (Markup), says what
 * they think of the whole of it, and says when they are done. When all of
 * them are, its step is done and Claude sums the review up for NSB (Jason, 2026-10-03).
 */

const ACCEPT = 'image/jpeg,image/png,image/webp,image/gif,application/pdf';

/** Images over this go up re-drawn at 4096 pixels across: plenty to review, and quick from a phone. */
const SEND_AS_IS = 8 * 1024 * 1024;
const LONGEST = 4096;

const day = (iso: string) => dateWords(iso, 'date');
const sourceHost = (url: string) => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return 'the web';
  }
};
const reviewsKey = (projectId: string) => ['project', projectId, 'reviews'];

export function Reviews() {
  const projectId = useProjectId();
  const { data: project } = useProject(projectId);
  const { data, error } = useQuery<ReviewSetDto[], ApiError>({
    queryKey: reviewsKey(projectId),
    queryFn: () => api<ReviewSetDto[]>(`/projects/${projectId}/reviews`),
    // A review being summed up: look again until it is.
    refetchInterval: (q) => (q.state.data?.some((s) => s.summary?.status === 'writing') ? 5000 : false),
  });
  useFeedbackRecord(projectId, project ? { type: 'project', id: projectId, label: `${project.name} — design reviews` } : null);
  // From a link to one iteration (?set=): brought into view once it is there.
  const [search] = useSearchParams();
  const target = search.get('set');
  const loaded = data !== undefined;
  useEffect(() => {
    if (!target || !loaded) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    document.getElementById(`set-${target}`)?.scrollIntoView({ block: 'start', behavior: reduce ? 'auto' : 'smooth' });
  }, [target, loaded]);
  if (error) return <LoadError error={error} />;
  if (!data || !project) {
    return (
      <Page>
        <Loading />
      </Page>
    );
  }
  const staff = project.viewerRole === 'contractor';
  const phases = reviewPhases(data);
  return (
    <Page wide>
      <ProjectCrumb project={project} />
      <Title
        how={
          staff
            ? {
                line: 'Design work for the client to mark up, by phase and iteration.',
                intent: 'Share design work with the client and gather their marks and comments, an iteration at a time.',
                steps: [
                  'Add a step that delivers a design review on the process; its iteration opens here, yours alone until you share it.',
                  'Add its files, then share it, choosing who reviews it.',
                  'When everyone asked has marked it done, its step is done and Claude sums up their comments for you.',
                ],
              }
            : {
                // Shown as a sheet of four steps, each with the thing itself as it looks on screen (Jason, 2026-10-06:
                // "this question mark should show a much more user friendly instructions as to how to engage in a design review").
                title: 'Reviewing a design',
                line: 'Drawings and renders for your comments.',
                intent: 'Tell NSB what you think of the design as it develops, on each drawing and on the whole of it.',
                steps: [
                  'Open the iteration NSB shared, then one of its files.',
                  'Choose Pin, Box, Circle or Draw, and press the drawing where you mean.',
                  'Write what you think. Reply keeps the conversation under your mark.',
                  `Press ${DONE_WORDS} once you have been through it. Every comment is kept.`,
                ],
                examples: reviewExamples(),
              }
        }
      >
        Design reviews
      </Title>
      {phases.length === 0 ? (
        staff ? (
          <p className="mt-10 text-ink-3">
            Nothing yet. A review starts when the job has a step that delivers one: add it in{' '}
            <Link to={`/p/${projectId}/settings/process`} className="link">
              the job’s process setup
            </Link>
            , and its iteration opens here.
          </p>
        ) : (
          <p className="mt-10 text-ink-3">Nothing has been shared with you yet.</p>
        )
      ) : null}
      {phases.map(({ phase, open, later }) => (
        <section key={phase} className="mt-12">
          <div className="border-b border-ink/40 pb-2">
            <h2 className="text-[1.375rem] sm:text-[1.5rem]">{phase}</h2>
          </div>
          <ol className="divide-y divide-rule">
            {open.map((r) => (
              <Review key={r.newest.id} projectId={projectId} review={r} staff={staff} target={target} />
            ))}
            {later.length ? (
              // Not reached yet (P-22): named, so what is coming is plain, and opened for its files once the work gets there.
              <li className="py-6">
                <p className="label">Later in the process</p>
                <ul className="mt-2 grid gap-1">
                  {later.map((s) => (
                    <li key={s.id} id={`set-${s.id}`} className="font-display text-[1.125rem] text-ink-2">
                      {s.title}
                    </li>
                  ))}
                </ul>
                <p className="mt-2 max-w-2xl text-sm text-ink-3">Each opens here for its files once the work reaches its step.</p>
              </li>
            ) : null}
          </ol>
        </section>
      ))}
    </Page>
  );
}

/**
 * One design review, the step that delivers it (P-22, design study 27, show A): its newest iteration in full, and each
 * earlier one a line that opens it in place; a link straight to an earlier one (?set=) opens it.
 */
function Review({ projectId, review: r, staff, target }: { projectId: string; review: ReviewGroup<ReviewSetDto>; staff: boolean; target: string | null }) {
  const [opened, setOpened] = useState<string | null>(r.earlier.some((s) => s.id === target) ? target : null);
  return (
    <li className="py-6">
      <Iteration projectId={projectId} set={r.newest} staff={staff} newest previous={r.earlier[0] ?? null} />
      {r.earlier.length ? (
        <ul className="mt-6 max-w-3xl border-t border-rule">
          {r.earlier.map((s) => (
            <li key={s.id} id={`set-${s.id}`} className="scroll-mt-6 border-b border-rule">
              <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 py-2">
                <p>
                  <span className="label mr-3">Iteration {s.iteration}</span>
                  {earlierLine(s)}
                </p>
                <Button tone="quiet" className="hit" onClick={() => setOpened(opened === s.id ? null : s.id)}>
                  {opened === s.id ? 'Close it' : 'Open it'}
                </Button>
              </div>
              {opened === s.id ? (
                <div className="pb-6">
                  <Iteration projectId={projectId} set={s} staff={staff} newest={false} previous={null} />
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </li>
  );
}

function Iteration({ projectId, set: s, staff, newest, previous }: { projectId: string; set: ReviewSetDto; staff: boolean; newest: boolean; previous: ReviewSetDto | null }) {
  const [sending, setSending] = useState<string | null>(null);
  const [sharing, setSharing] = useState(false);
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const draft = !s.sharedAt;
  const stepLine = reviewStepLine(s);
  const sheets = sheetGroups(s.files);
  // Sheets go into a shared iteration while it is its review's newest (design study 29); reviewed, they open it again.
  const takesSheets = !draft && newest;

  const run = async (fn: () => Promise<ReviewSetDto[]>) => {
    setBusy(true);
    setErr(null);
    try {
      queryClient.setQueryData(reviewsKey(projectId), await fn());
      // The review task, the home's needs, the client's waiting list and the project's files move with it.
      await queryClient.invalidateQueries({ queryKey: ['project', projectId], predicate: (q) => q.queryKey[2] !== 'reviews' });
      return true;
    } catch (e) {
      setErr(errorText(e));
      return false;
    } finally {
      setBusy(false);
    }
  };

  /**
   * A PDF added to the iteration becomes its sheets (P-36, design study 28 A; P-37: the PDF itself does not go in beside
   * them): each page drawn as a picture and sent through the same door as one added by hand, in the PDF's order; a set
   * that cannot be split goes in whole (`sendWhole`). pdf.js loads only now. What went wrong, in words; null when every
   * sheet is a plate.
   */
  const splitIntoPlates = async (pdf: Blob, pdfName: string, sendWhole: () => Promise<void>): Promise<string | null> => {
    setSending(`Reading ${pdfName}`);
    try {
      return await addDrawingSet(pdfName, s.title, {
        open: async () => {
          const reader = await import('./pdfSet.ts').catch(() => {
            throw new ReaderMissing();
          });
          return reader.openPdf(await pdf.arrayBuffer());
        },
        sendPlate: async (name, plate) => {
          queryClient.setQueryData(reviewsKey(projectId), await upload<ReviewSetDto[]>(`/projects/${projectId}/reviews/${s.id}/files?name=${encodeURIComponent(name)}`, plate, 'image/jpeg'));
        },
        sendWhole,
        saying: setSending,
      });
    } finally {
      void queryClient.invalidateQueries({ queryKey: assetsKey(projectId) });
    }
  };

  const send = async (files: File[]) => {
    setErr(null);
    const problems: string[] = [];
    for (const [i, file] of files.entries()) {
      setSending(files.length > 1 ? `Sending ${i + 1} of ${files.length}: ${file.name}` : `Sending ${file.name}`);
      const whole = async () => {
        const ready = await fitForUpload(file);
        queryClient.setQueryData(
          reviewsKey(projectId),
          await upload<ReviewSetDto[]>(`/projects/${projectId}/reviews/${s.id}/files?name=${encodeURIComponent(ready.name)}`, ready.body, ready.type),
        );
        void queryClient.invalidateQueries({ queryKey: assetsKey(projectId) });
      };
      try {
        // A drawing set: its pages become plates, to mark up, in its place.
        if (splitsIntoPlates({ type: typeOf(file), name: file.name })) {
          const trouble = await splitIntoPlates(file, file.name, whole);
          if (trouble) problems.push(trouble);
        } else await whole();
      } catch (e) {
        problems.push(`${file.name}: ${e instanceof ApiError && e.status === 413 ? 'too big to send. Keep files under 60 MB.' : errorText(e)}`);
        break;
      }
    }
    setSending(null);
    if (problems.length) setErr(problems.join(' '));
  };

  /** Files from the project's files, one at a time in the order picked, so a PDF's plates come in its place (P-36, P-37). */
  const addFromFiles = async (picked: AssetDto[]): Promise<boolean> => {
    const problems: string[] = [];
    try {
      for (const a of picked) {
        const whole = async () => {
          if (!(await run(() => api<ReviewSetDto[]>(`/projects/${projectId}/reviews/${s.id}/assets`, { body: { assetIds: [a.id] } })))) throw new Error('refused');
        };
        if (a.kind !== 'file' || !a.originalUrl || !splitsIntoPlates({ type: a.contentType ?? '', name: a.name })) {
          if (!(await whole().then(() => true, () => false))) return false;
          continue;
        }
        setSending(`Reading ${a.name}`);
        const pdf = await download(a.originalUrl.replace(/^\/api/, '')).catch(() => null);
        const trouble = pdf ? await splitIntoPlates(pdf, a.name, whole).catch(() => null) : await whole().then(() => splitFailed(a.name, null), () => null);
        if (trouble) problems.push(trouble);
      }
      return true;
    } finally {
      setSending(null);
      // After any refusal `run` showed, never in place of it.
      if (problems.length) setErr((shown) => [shown, ...problems].filter(Boolean).join(' '));
    }
  };

  return (
    <div id={newest ? `set-${s.id}` : undefined} className="scroll-mt-6">
      <IterationHead set={s} staff={staff} />
      {s.note && shareNoteLabel(s) ? (
        // NSB's note, written as it shared the review and sent with its email, kept as it was sent (P-24).
        <div className="mt-3 max-w-2xl border-l-2 border-rule pl-3">
          <p className="label">{shareNoteLabel(s)}</p>
          <p className="mt-1 whitespace-pre-line">{s.note}</p>
        </div>
      ) : s.note ? (
        <p className="mt-2 max-w-2xl whitespace-pre-line text-ink-2">{s.note}</p>
      ) : null}
      {roundNotes(s.files).map((r) => (
        // NSB's note with sheets sent after it was shared (design study 29), kept as it was sent.
        <div key={r.id} className="mt-3 max-w-2xl border-l-2 border-rule pl-3">
          <p className="label">{r.label}</p>
          <p className="mt-1 whitespace-pre-line">{r.note}</p>
        </div>
      ))}
      {staff && stepLine ? (
        <p className="mt-2 max-w-2xl text-ink-2">
          {stepLine}{' '}
          <Link to={`/p/${projectId}/process`} className="link">
            Open the process
          </Link>
        </p>
      ) : null}

      {sheets.standing.length + sheets.revised.length ? (
        <ul className="mt-4 grid grid-cols-2 gap-x-4 gap-y-5 sm:grid-cols-3 lg:grid-cols-4">
          {[...sheets.standing, ...sheets.revised].map((f) => (
            <FileTile
              key={f.id}
              projectId={projectId}
              file={f}
              revised={f.revisedBy !== null}
              revisedDay={revisedOn(f, s.files)}
              onTakeOut={draft && staff ? () => void run(() => api<ReviewSetDto[]>(`/projects/${projectId}/reviews/files/${f.id}`, { method: 'DELETE' })) : null}
            />
          ))}
        </ul>
      ) : staff && !sheets.unsent.length ? (
        <p className="mt-3 text-ink-3">No files yet.</p>
      ) : null}

      {staff && sheets.unsent.length ? <NotSent projectId={projectId} set={s} held={sheets.unsent} standing={sheets.standing} busy={busy} run={run} /> : null}

      {staff && (draft || takesSheets) ? (
        <div className="mt-5 max-w-3xl">
          <DropZone
            compact
            tone={draft ? 'plain' : 'shared'}
            label="Drop images or PDFs here"
            accept={ACCEPT}
            busy={sending !== null}
            onFiles={(files) => void send(files)}
            hint={
              sending ? (
                <span className="motion-safe:animate-pulse">{sending}</span>
              ) : draft ? (
                'Large images go up at 4096 pixels across. Each page of a PDF becomes a plate beside it, to mark up.'
              ) : (
                <>
                  <span className="text-cedar-deep">{heldHint(s.reviewers)}</span> Each page of a PDF becomes a plate beside it.
                </>
              )
            }
          >
            {!picking ? (
              <Button tone="quiet" onClick={() => setPicking(true)}>
                From the project’s files
              </Button>
            ) : null}
          </DropZone>
          {draft && s.files.length && canShare(s) && !sharing ? (
            <div className="mt-4">
              <Button disabled={sending !== null} onClick={() => setSharing(true)}>
                Share it for review
              </Button>
            </div>
          ) : null}
          {picking ? (
            <FromFiles
              projectId={projectId}
              onAdd={addFromFiles}
              onDone={() => setPicking(false)}
            />
          ) : null}
          {sharing ? (
            <ShareWith
              projectId={projectId}
              asked={previous?.reviewers.map((r) => r.contactId) ?? null}
              busy={busy}
              onShare={(reviewers, note) => void run(() => api<ReviewSetDto[]>(`/projects/${projectId}/reviews/${s.id}/share`, { body: { reviewers, note } })).then((ok) => ok && setSharing(false))}
              onCancel={() => setSharing(false)}
            />
          ) : null}
        </div>
      ) : null}

      {!draft ? <Reviewers projectId={projectId} set={s} staff={staff} busy={busy} run={run} /> : null}
      {!draft ? <Closed projectId={projectId} set={s} staff={staff} busy={busy} run={run} /> : null}
      {staff && newest && s.reviewed && reviewStep(s) === 'on' ? (
        // The next iteration, once this one is reviewed (P-22, design study 27: start A, why A): no reason asked.
        <div className="mt-4 max-w-2xl">
          <p className="text-ink-2">
            A sheet or two revised? Drop them above: they join this iteration beside what they revise. A new set instead? Start the next iteration of this review: new files,
            shared again with the same people. Its step is open again until that one is reviewed.
          </p>
          <div className="mt-3">
            <Button tone="secondary" busy={busy} onClick={() => void run(() => api<ReviewSetDto[]>(`/projects/${projectId}/reviews/${s.id}/next`, { body: {} }))}>
              Start iteration {s.iteration + 1}
            </Button>
          </div>
        </div>
      ) : null}
      {!draft ? <WholeOfIt projectId={projectId} set={s} busy={busy} run={run} /> : null}
      {staff && s.summary ? <Summary projectId={projectId} set={s} busy={busy} run={run} /> : null}
      <ErrorLine error={err} />
    </div>
  );
}

type Run = (fn: () => Promise<ReviewSetDto[]>) => Promise<boolean>;

/**
 * Who is asked to review it, chosen from the project's people as it is shared: everyone who can sign in, to begin with;
 * and a note for them if NSB likes, sent in their email and kept at the top of the review as it was sent (P-24).
 */
function ShareWith({
  projectId,
  asked,
  busy,
  onShare,
  onCancel,
}: {
  projectId: string;
  /** The people asked about the iteration before it, asked again by default (P-22). */
  asked: string[] | null;
  busy: boolean;
  onShare: (reviewers: string[], note: string) => void;
  onCancel: () => void;
}) {
  const { data } = useApi<{ contacts: ContactDto[] }>(['project', projectId, 'people'], `/projects/${projectId}/people`);
  const [picked, setPicked] = useState<string[] | null>(null);
  const [note, setNote] = useState('');
  if (!data) return <Loading />;
  const people = data.contacts;
  const signIn = people.filter((c) => c.invitedAt).map((c) => c.id);
  const again = asked?.filter((id) => people.some((c) => c.id === id)) ?? [];
  const chosen = picked ?? (again.length ? again : signIn.length ? signIn : people.filter((c) => c.isPrimary).map((c) => c.id));
  return (
    <div className="mt-5 max-w-2xl border-t border-ink/40 pt-3">
      <p className="font-display text-[1.25rem]">Who reviews it?</p>
      <p className="mt-1 text-sm text-ink-2">
        The client’s side sees these files from now on, and shared files stay. Each person you ask marks it done when they are; it is reviewed when all of them have, and Claude sums up their comments for you.
      </p>
      {people.length === 0 ? (
        <p className="mt-3">
          Nobody is on the project yet.{' '}
          <Link to={`/p/${projectId}/people`} className="link">
            Add them on People
          </Link>
          .
        </p>
      ) : (
        <ul className="mt-3 divide-y divide-rule border-y border-rule">
          {people.map((c) => (
            <li key={c.id}>
              <label className="flex min-h-11 cursor-pointer items-center gap-3 py-1.5">
                <input type="checkbox" checked={chosen.includes(c.id)} onChange={(e) => setPicked(e.target.checked ? [...chosen, c.id] : chosen.filter((x) => x !== c.id))} />
                <span className="min-w-0 flex-1">{c.name}</span>
                <span className="meta shrink-0">{c.lastLoginAt ? 'Signs in' : c.invitedAt ? 'Invited' : 'No sign-in yet'}</span>
              </label>
            </li>
          ))}
        </ul>
      )}
      {chosen.some((id) => !people.find((c) => c.id === id)?.invitedAt) ? (
        <p className="meta mt-2">Someone without a sign-in cannot mark it done themselves: invite them from People, or mark it for them here once they have told you.</p>
      ) : null}
      <div className="mt-5">
        <Field label="A note for them" hint="It goes in the email that tells them, and stays at the top of this review as it was sent.">
          {(id) => (
            <textarea
              id={id}
              rows={3}
              maxLength={4000}
              className="field resize-y"
              placeholder="What these files are, and what to look at in them"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          )}
        </Field>
      </div>
      <div className="flex flex-wrap gap-5">
        <Button busy={busy} disabled={!chosen.length} onClick={() => onShare(chosen, note)}>
          {chosen.length > 1 ? `Share it with these ${chosen.length}` : 'Share it'}
        </Button>
        <Button tone="quiet" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

/**
 * One person asked and where they stand; for NSB, not done yet, when they were emailed of it and Remind them (design
 * study 26), and Mark them done.
 */
function Reviewer({ projectId, set: s, reviewer: r, staff, onMarkDone }: { projectId: string; set: ReviewSetDto; reviewer: ReviewerDto; staff: boolean; onMarkDone: () => void }) {
  const remind = useRemind(projectId, 'review', s.id, { staff: staff && !r.doneAt && r.canSignIn, contactId: r.contactId });
  const [told] = remind.people;
  return (
    <li className="flex min-h-11 flex-wrap items-center justify-between gap-x-4 py-1.5">
      <span>
        {r.name}{' '}
        <span className="meta">
          {r.doneAt ? `done ${day(r.doneAt)}${r.doneBy ? `, marked by ${r.doneBy}` : ''}` : staff && !r.canSignIn ? 'not done yet · no sign-in' : 'not done yet'}
          {askedAgainWords(r) ? ` · ${askedAgainWords(r)}` : null}
          {told ? ` · ${personLine(told, remind.today)}` : null}
        </span>
      </span>
      {staff && !r.doneAt && !remind.open ? (
        <span className="flex flex-wrap gap-x-5">
          <RemindLink r={remind} />
          <button type="button" className="link min-h-11 text-sm text-ink-2" onClick={onMarkDone}>
            Mark them done
          </button>
        </span>
      ) : null}
      <RemindAsk r={remind} />
    </li>
  );
}

/** The people asked and where each stands; the viewer's own part; NSB changing who is asked, or saying one of them is done. */
function Reviewers({ projectId, set: s, staff, busy, run }: { projectId: string; set: ReviewSetDto; staff: boolean; busy: boolean; run: Run }) {
  const [finishing, setFinishing] = useState(false);
  const [changing, setChanging] = useState(false);
  const [doneFor, setDoneFor] = useState<ReviewerDto | null>(null);
  const done = (body: { contactId?: string; comment?: string }) => run(() => api<ReviewSetDto[]>(`/projects/${projectId}/reviews/${s.id}/done`, { body }));
  return (
    <div className="mt-5 max-w-2xl">
      {s.reviewers.length ? (
        <>
          <p className="label mb-1">{s.reviewed ? 'Reviewed by' : 'Asked to review it'}</p>
          <ul className="divide-y divide-rule border-y border-rule">
            {s.reviewers.map((r) => (
              <Reviewer key={r.contactId} projectId={projectId} set={s} reviewer={r} staff={staff} onMarkDone={() => setDoneFor(r)} />
            ))}
          </ul>
        </>
      ) : staff ? (
        <p className="text-ink-3">Nobody is asked to review it.</p>
      ) : null}
      {doneFor ? (
        <Confirm
          prompt={`Mark ${doneFor.name} done reviewing it? Only when they have told you so.`}
          busy={busy}
          onYes={() => void done({ contactId: doneFor.contactId }).then((ok) => ok && setDoneFor(null))}
          onNo={() => setDoneFor(null)}
        />
      ) : null}
      {staff && !changing && !s.reviewed ? (
        <button type="button" className="link mt-1 min-h-11 text-sm text-ink-2" onClick={() => setChanging(true)}>
          Change who reviews it
        </button>
      ) : null}
      {staff && changing ? <ChangeReviewers projectId={projectId} set={s} busy={busy} run={run} onDone={() => setChanging(false)} /> : null}

      {s.yours === 'waiting' && !finishing ? (
        <div className="mt-4">
          <Button onClick={() => setFinishing(true)}>{DONE_WORDS}</Button>
        </div>
      ) : null}
      {s.yours === 'waiting' && finishing ? (
        <Finish title={s.title} busy={busy} onDone={(comment) => void done(comment ? { comment } : {}).then((ok) => ok && setFinishing(false))} onCancel={() => setFinishing(false)} />
      ) : null}
      {s.yours === 'done' ? <p className="meta mt-3">You marked it done. You can still comment, on a file or on the whole of it.</p> : null}
    </div>
  );
}

/**
 * Reviewed by everyone asked: closed to comments (Jason, 2026-10-03: "once a
 * concept iteration is marked reviewed it's locked for further commentary
 * unless it is unlocked again"). NSB can open it again, saying why, as a gate
 * is passed on override; that starts a new round for the reviewers.
 */
function Closed({ projectId, set: s, staff, busy, run }: { projectId: string; set: ReviewSetDto; staff: boolean; busy: boolean; run: Run }) {
  const [asking, setAsking] = useState(false);
  const [why, setWhy] = useState('');
  if (!s.reviewed) {
    return s.reopened ? (
      <p className="meta mt-3 max-w-2xl">
        Opened again for comments by {s.reopened.by}, {day(s.reopened.at)}: “{s.reopened.reason}”
      </p>
    ) : null;
  }
  return (
    <div className="mt-4 max-w-2xl border-l-2 border-moss pl-3">
      <p className="text-[0.9375rem]">Everyone asked has reviewed it, so it is closed to comments.</p>
      {staff && !asking ? (
        <button type="button" className="link mt-1 min-h-11 text-sm text-ink-2 hover:text-ink" onClick={() => setAsking(true)}>
          Open it again for comments
        </button>
      ) : null}
      {staff && asking ? (
        <div className="mt-2">
          <Field label="Why it opens again" hint="The reviewers are asked again, and see why.">
            {(id) => <textarea id={id} rows={2} className="field resize-y" value={why} onChange={(e) => setWhy(e.target.value)} />}
          </Field>
          <div className="flex flex-wrap gap-5">
            <Button
              busy={busy}
              disabled={!why.trim()}
              onClick={() =>
                void run(() => api<ReviewSetDto[]>(`/projects/${projectId}/reviews/${s.id}/unlock`, { body: { reason: why.trim() } })).then((ok) => {
                  if (!ok) return;
                  setAsking(false);
                  setWhy('');
                })
              }
            >
              Open it again
            </Button>
            <Button tone="quiet" onClick={() => setAsking(false)}>
              Keep it closed
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Saying you are done, asked once, with room for what you think of the whole of it. */
function Finish({ title, busy, onDone, onCancel }: { title: string; busy: boolean; onDone: (comment: string) => void; onCancel: () => void }) {
  const [words, setWords] = useState('');
  return (
    <div className="mt-4 border-t border-ink/40 pt-3">
      <p className="font-display text-[1.25rem]">Done reviewing {title}?</p>
      <p className="mb-4 mt-1 text-sm text-ink-2">NSB is told, and sees all your comments. You can still add comments afterwards.</p>
      <Field label="Anything about the whole of it?" hint="Optional: what you think of it overall, what matters most, what to keep.">
        {(id) => <textarea id={id} rows={4} className="field resize-y" value={words} onChange={(e) => setWords(e.target.value)} />}
      </Field>
      <div className="flex flex-wrap gap-5">
        <Button busy={busy} onClick={() => onDone(words.trim())}>
          Yes, I’m done
        </Button>
        <Button tone="quiet" onClick={onCancel}>
          Not yet
        </Button>
      </div>
    </div>
  );
}

/** NSB changing who reviews it: anyone done stays ticked. */
function ChangeReviewers({ projectId, set: s, busy, run, onDone }: { projectId: string; set: ReviewSetDto; busy: boolean; run: Run; onDone: () => void }) {
  const { data } = useApi<{ contacts: ContactDto[] }>(['project', projectId, 'people'], `/projects/${projectId}/people`);
  const [chosen, setChosen] = useState<string[]>(s.reviewers.map((r) => r.contactId));
  const save = () => run(() => api<ReviewSetDto[]>(`/projects/${projectId}/reviews/${s.id}/reviewers`, { method: 'PUT', body: { contactIds: chosen } })).then((ok) => ok && onDone());
  // Who reviews it, changed and not saved, on the bar at the window's foot, which saves as Save below does (P-29).
  useUnsaved({
    names: sameValue([...chosen].sort(), s.reviewers.map((r) => r.contactId).sort()) ? [] : ['Who reviews it'],
    busy,
    canSave: chosen.length > 0,
    onSave: save,
    onDiscard: () => setChosen(s.reviewers.map((r) => r.contactId)),
  });
  if (!data) return <Loading />;
  const finished = new Set(s.reviewers.filter((r) => r.doneAt).map((r) => r.contactId));
  return (
    <div className="mt-3 border-t border-ink/40 pt-3">
      <p className="label mb-1">Who reviews it</p>
      <ul className="divide-y divide-rule border-y border-rule">
        {data.contacts.map((c) => (
          <li key={c.id}>
            <label className={`flex min-h-11 items-center gap-3 py-1.5 ${finished.has(c.id) ? '' : 'cursor-pointer'}`}>
              <input
                type="checkbox"
                checked={chosen.includes(c.id)}
                disabled={finished.has(c.id)}
                onChange={(e) => setChosen(e.target.checked ? [...chosen, c.id] : chosen.filter((x) => x !== c.id))}
              />
              <span className="min-w-0 flex-1">{c.name}</span>
              <span className="meta shrink-0">{finished.has(c.id) ? 'Done, so stays' : c.lastLoginAt ? 'Signs in' : c.invitedAt ? 'Invited' : 'No sign-in yet'}</span>
            </label>
          </li>
        ))}
      </ul>
      <p className="meta mt-2">Anyone new is asked from now.</p>
      <div className="mt-3 flex flex-wrap gap-5">
        <Button busy={busy} disabled={!chosen.length} onClick={() => void save()}>
          Save
        </Button>
        <Button tone="quiet" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

/** What was said of the iteration as a whole, oldest first, and a word to add to it. */
function WholeOfIt({ projectId, set: s, busy, run }: { projectId: string; set: ReviewSetDto; busy: boolean; run: Run }) {
  const [open, setOpen] = useState(false);
  const [words, setWords] = useState('');
  const add = () =>
    run(() => api<ReviewSetDto[]>(`/projects/${projectId}/reviews/${s.id}/comments`, { body: { body: words.trim() } })).then((ok) => {
      if (!ok) return;
      setWords('');
      setOpen(false);
    });
  return (
    <section className="mt-6 max-w-2xl" aria-label="On the whole of it">
      <p className="label mb-1">On the whole of it</p>
      {s.reviewed && !s.comments.length ? <p className="text-ink-3">No comments on the whole of it.</p> : null}
      {s.comments.length ? (
        <ul className="divide-y divide-rule border-y border-rule">
          {s.comments.map((c) => (
            <li key={c.id} className="py-3">
              <p className="meta">
                {c.mine ? 'You' : c.authorName}
                {c.authorRole === 'contractor' ? ' · NSB' : ''} · <span className="num">{day(c.createdAt)}</span>
              </p>
              <p className="mt-0.5 whitespace-pre-line">{c.body}</p>
            </li>
          ))}
        </ul>
      ) : null}
      {s.reviewed ? null : open ? (
        <div className="mt-3">
          <textarea autoFocus rows={3} className="field resize-y" aria-label="A comment on the whole of it" value={words} onChange={(e) => setWords(e.target.value)} />
          <div className="mt-2 flex flex-wrap gap-5">
            <Button busy={busy} disabled={!words.trim()} onClick={() => void add()}>
              Add it
            </Button>
            <Button tone="quiet" onClick={() => setOpen(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <button type="button" className="link mt-1 min-h-11 text-sm text-ink-2" onClick={() => setOpen(true)}>
          {s.comments.length ? 'Add to it' : 'Comment on the whole of it'}
        </button>
      )}
    </section>
  );
}

/** Claude's reading of the finished review, for NSB: each point opens on the comments it comes from. */
function Summary({ projectId, set: s, busy, run }: { projectId: string; set: ReviewSetDto; busy: boolean; run: Run }) {
  const m = s.summary!;
  const fileLink = (fileId: string, threadId?: string) => `/p/${projectId}/reviews/files/${fileId}${threadId ? `?thread=${threadId}` : ''}`;
  return (
    <section className="mt-8 max-w-3xl" aria-label="The review, summed up">
      <div className="flex flex-wrap items-baseline justify-between gap-x-6 border-b border-ink/40 pb-2">
        <h4 className="font-display text-[1.25rem]">The review, summed up</h4>
        <span className="label">{m.status === 'ready' ? `Summed up by Claude · ${day(m.createdAt)}` : m.status === 'writing' ? 'Being summed up' : 'Not summed up'}</span>
      </div>
      {m.status === 'writing' ? <p className="mt-3 text-ink-2 motion-safe:animate-pulse">Claude is reading every comment and mark. A minute or so.</p> : null}
      {m.status === 'failed' ? <p className="mt-3 text-ink-2">{m.error}</p> : null}
      {m.status === 'ready' ? (
        <>
          {m.overall ? <p className="mt-3 max-w-measure text-[1.0625rem]">{m.overall}</p> : null}
          {m.points.length ? (
            <ul className="mt-4 divide-y divide-rule border-y border-rule">
              {m.points.map((pt, i) => (
                <li key={i} className="py-3">
                  <p>{pt.text}</p>
                  <p className="meta mt-0.5">
                    {pt.fileId ? (
                      <Link to={fileLink(pt.fileId)} className="link">
                        {pt.fileName}
                      </Link>
                    ) : (
                      (pt.fileName ?? 'On the whole of it')
                    )}
                    {pt.fileId
                      ? pt.marks.map((mk) => (
                          <span key={mk.threadId}>
                            {' · '}
                            <Link to={fileLink(pt.fileId!, mk.threadId)} className="link">
                              comment {mk.number}
                            </Link>
                          </span>
                        ))
                      : null}
                  </p>
                </li>
              ))}
            </ul>
          ) : null}
          {m.questions.length ? (
            <>
              <p className="label mb-1 mt-5">Asked, and waiting on an answer</p>
              <ul className="list-disc space-y-1 pl-5">
                {m.questions.map((q) => (
                  <li key={q}>{q}</li>
                ))}
              </ul>
            </>
          ) : null}
          {m.next.length ? (
            <>
              <p className="label mb-1 mt-5">For the next iteration</p>
              <ul className="list-disc space-y-1 pl-5">
                {m.next.map((q) => (
                  <li key={q}>{q}</li>
                ))}
              </ul>
            </>
          ) : null}
          <p className="meta mt-4">Claude’s reading of the comments; the comments themselves are the record.</p>
        </>
      ) : null}
      {m.status !== 'writing' ? (
        <div className="mt-3 flex flex-wrap items-center gap-x-5">
          {!m.read ? (
            <Button tone="secondary" busy={busy} onClick={() => void run(() => api<ReviewSetDto[]>(`/projects/${projectId}/reviews/${s.id}/summary/read`, { body: {} }))}>
              Got it
            </Button>
          ) : null}
          <Button tone="quiet" busy={busy} onClick={() => void run(() => api<ReviewSetDto[]>(`/projects/${projectId}/reviews/${s.id}/summary`, { body: {} }))}>
            {m.status === 'failed' ? 'Try again' : 'Sum it up again'}
          </Button>
        </div>
      ) : null}
    </section>
  );
}

/** Files already on the project, picked into this iteration. */
function FromFiles({ projectId, onAdd, onDone }: { projectId: string; onAdd: (picked: AssetDto[]) => Promise<boolean>; onDone: () => void }) {
  const { data } = useApi<AssetDto[]>(assetsKey(projectId), `/projects/${projectId}/assets`);
  const [picked, setPicked] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const free = (data ?? []).filter((a) => !a.review);
  const add = async () => {
    setBusy(true);
    const ok = await onAdd(picked.map((id) => free.find((a) => a.id === id)).filter((a): a is AssetDto => a !== undefined));
    setBusy(false);
    if (ok) onDone();
  };
  return (
    <div className="mt-5 max-w-2xl border border-rule bg-sheet p-4 sm:p-5">
      <p className="label mb-2">From the project’s files</p>
      {!data ? (
        <Loading />
      ) : free.length === 0 ? (
        <p className="py-2 text-ink-3">Everything in the project’s files is in an iteration already. Add new files here, or in Files.</p>
      ) : (
        <ul className="divide-y divide-rule">
          {free.map((a) => (
            <li key={a.id}>
              <label className="flex min-h-11 cursor-pointer items-center gap-3 py-1.5">
                <input
                  type="checkbox"
                  checked={picked.includes(a.id)}
                  onChange={(e) => setPicked(e.target.checked ? [...picked, a.id] : picked.filter((x) => x !== a.id))}
                />
                {a.thumbUrl ? <img src={a.thumbUrl} alt="" loading="lazy" className="h-9 w-12 shrink-0 rounded-[2px] bg-inset object-cover" /> : null}
                <span className="min-w-0 flex-1 truncate">{a.name}</span>
                {a.shared ? <span className="label shrink-0">Shared</span> : null}
              </label>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-4 flex gap-5">
        <Button busy={busy} disabled={!picked.length} onClick={() => void add()}>
          {picked.length > 1 ? `Add these ${picked.length}` : 'Add it'}
        </Button>
        <Button tone="quiet" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

function FileTile({
  projectId,
  file: f,
  onTakeOut,
  revised = false,
  revisedDay = null,
}: {
  projectId: string;
  file: ReviewFileDto;
  onTakeOut: (() => void) | null;
  /** Revised since (design study 29, earlier B): below the sheets as they stand, greyed, and named so. */
  revised?: boolean;
  revisedDay?: string | null;
}) {
  const [confirming, setConfirming] = useState(false);
  return (
    <li className="min-w-0">
      <Link to={`/p/${projectId}/reviews/files/${f.id}`} className="group block">
        <span className="relative block">
          {f.thumbUrl ? (
            <img src={f.thumbUrl} alt="" loading="lazy" className={`aspect-[4/3] w-full rounded-[2px] bg-inset object-cover ${revised ? 'opacity-60' : ''}`} />
          ) : (
            <span className="flex aspect-[4/3] w-full items-center justify-center rounded-[2px] border border-rule bg-sheet">
              <span className="label">{f.kind === 'link' ? 'Link' : f.contentType === 'application/pdf' ? 'PDF' : 'File'}</span>
            </span>
          )}
          {revised ? (
            <span className="label absolute left-2 top-2 rounded-[2px] bg-paper/95 px-1.5 py-0.5 !text-ink">{revisedDay ? `Revised ${dateWords(revisedDay, 'dayOrDate')}` : 'Revised'}</span>
          ) : null}
        </span>
        <span className={`mt-1.5 block truncate group-hover:underline ${revised ? 'text-ink-2' : ''}`}>{f.name}</span>
      </Link>
      {f.revises ? <span className="meta block truncate">Revises {f.revises.name}</span> : null}
      <span className="meta block">
        {f.threads === 0 ? 'No comments yet' : `${f.threads} comment${f.threads === 1 ? '' : 's'}${f.openThreads ? `, ${f.openThreads} open` : ', all resolved'}`}
      </span>
      {onTakeOut ? (
        confirming ? (
          <span className="flex flex-wrap items-center gap-x-4 text-sm">
            Take it out? It stays in the project’s files.
            <Button tone="danger" onClick={onTakeOut}>
              Yes
            </Button>
            <Button tone="quiet" onClick={() => setConfirming(false)}>
              No
            </Button>
          </span>
        ) : (
          <button type="button" className="link min-h-11 text-sm text-ink-3 hover:text-alert" onClick={() => setConfirming(true)}>
            Take out
          </button>
        )
      ) : null}
    </li>
  );
}

/**
 * Sheets put into an iteration already shared, not sent yet (design study 29): NSB's alone, each saying whether it
 * revises a sheet the reviewers have (the earlier one stays, every comment on it kept), sent together with a note.
 */
function NotSent({
  projectId,
  set: s,
  held,
  standing,
  busy,
  run,
}: {
  projectId: string;
  set: ReviewSetDto;
  held: ReviewFileDto[];
  standing: ReviewFileDto[];
  busy: boolean;
  run: Run;
}) {
  const [revises, setRevises] = useState<Record<string, string>>({});
  const [note, setNote] = useState('');
  // A sheet is revised by one sheet at most: one chosen for a sheet is not offered for another.
  const chosen = (except: string) => new Set(Object.entries(revises).flatMap(([k, v]) => (k !== except && v ? [v] : [])));
  const send = () =>
    run(() =>
      api<ReviewSetDto[]>(`/projects/${projectId}/reviews/${s.id}/send`, {
        body: {
          revises: held.flatMap((f) => (revises[f.id] ? [{ fileId: f.id, revisesId: revises[f.id] }] : [])),
          note: note.trim() || null,
        },
        notice: 'Sent',
      }),
    ).then((ok) => {
      if (!ok) return;
      setRevises({});
      setNote('');
    });
  return (
    <section className="mt-6 max-w-3xl border-l-2 border-cedar pl-4" aria-label="Not sent yet">
      <p className="label !text-cedar-deep">Not sent yet</p>
      <p className="mt-1 text-ink-2">Only you see {held.length === 1 ? 'it' : 'these'} until you send {held.length === 1 ? 'it' : 'them'}. Say what each revises, if anything: the earlier sheet stays, with every comment on it.</p>
      <ul className="mt-3 divide-y divide-rule border-y border-rule">
        {held.map((f) => (
          <li key={f.id} className="flex flex-wrap items-end gap-x-5 gap-y-2 py-3">
            <Link to={sheetPage(projectId, f.id)} className="group flex min-w-0 flex-1 basis-56 items-center gap-3">
              {f.thumbUrl ? (
                <img src={f.thumbUrl} alt="" className="h-12 w-16 shrink-0 rounded-[2px] bg-inset object-cover" />
              ) : (
                <span className="flex h-12 w-16 shrink-0 items-center justify-center rounded-[2px] border border-rule bg-sheet">
                  <span className="label">{f.contentType === 'application/pdf' ? 'PDF' : 'File'}</span>
                </span>
              )}
              <span className="min-w-0 truncate group-hover:underline">{f.name}</span>
            </Link>
            <div className="w-full sm:w-64">
              <label className="label block" htmlFor={`revises-${f.id}`}>
                Revises
              </label>
              <select id={`revises-${f.id}`} className="field" value={revises[f.id] ?? ''} onChange={(e) => setRevises({ ...revises, [f.id]: e.target.value })}>
                <option value="">Nothing: a new sheet</option>
                {standing
                  .filter((x) => !chosen(f.id).has(x.id))
                  .map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.name}
                    </option>
                  ))}
              </select>
            </div>
            <button
              type="button"
              className="link min-h-11 text-sm text-ink-3 hover:text-alert"
              onClick={() => void run(() => api<ReviewSetDto[]>(`/projects/${projectId}/reviews/files/${f.id}`, { method: 'DELETE' }))}
            >
              Take out
            </button>
          </li>
        ))}
      </ul>
      <div className="mt-4">
        <Field label="A note for them" hint="It goes in the email that tells them, and stays on the review as it was sent.">
          {(id) => <textarea id={id} rows={3} className="field resize-y" value={note} onChange={(e) => setNote(e.target.value)} placeholder="What changed, and what to look at" />}
        </Field>
      </div>
      <p className="mb-3 text-ink-2">{sendLine(s)}</p>
      <Button busy={busy} onClick={() => void send()}>
        {sendWords(s.reviewers, held.length)}
      </Button>
    </section>
  );
}

/** The file's type: as the browser says, else from its name. */

/** A large image re-drawn at 4096 pixels across as a JPEG (on white, for any transparency); anything else as it is. */
async function fitForUpload(file: File): Promise<{ body: Blob; type: string; name: string }> {
  const type = typeOf(file);
  if (!/^image\/(jpeg|png|webp)$/.test(type) || file.size <= SEND_AS_IS) return { body: file, type, name: file.name };
  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    const scale = Math.min(1, LONGEST / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    const g = canvas.getContext('2d')!;
    g.fillStyle = '#ffffff';
    g.fillRect(0, 0, canvas.width, canvas.height);
    g.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9));
    if (!blob) return { body: file, type, name: file.name };
    return { body: blob, type: 'image/jpeg', name: `${file.name.replace(/\.(png|webp|jpe?g)$/i, '')}.jpg` };
  } catch {
    return { body: file, type, name: file.name };
  }
}

/**
 * One file, marked up: the image and its marks, the comments beside it, the
 * history under them. Any of the project's files opens here, in a review
 * iteration or not; from Files, it goes back there.
 */
export function ReviewFile() {
  const projectId = useProjectId();
  const fileId = useParams<{ fileId: string }>().fileId!;
  const [search] = useSearchParams();
  const key = ['project', projectId, 'review-file', fileId];
  const { data, error } = useApi<FileMarksDto>(key, `/projects/${projectId}/reviews/files/${fileId}/marks`);
  // The sheet it revises, read for the compare (design study 29).
  const earlierId = data?.file.revises?.id ?? null;
  const { data: earlier } = useApi<FileMarksDto>(['project', projectId, 'review-file', earlierId ?? 'none'], earlierId ? `/projects/${projectId}/reviews/files/${earlierId}/marks` : null);
  useFeedbackRecord(projectId, data ? { type: 'review_file', id: fileId, label: `${data.file.setTitle ?? 'Files'} — ${data.file.name}` } : null);
  if (error) return <LoadError error={error} />;
  if (!data) {
    return (
      <Page wide>
        <Loading />
      </Page>
    );
  }
  const siblings = data.file.siblings;
  const at = siblings.findIndex((s) => s.id === fileId);
  const prev = at > 0 ? siblings[at - 1] : undefined;
  const next = at >= 0 && at < siblings.length - 1 ? siblings[at + 1] : undefined;
  const inSet = data.file.setId !== null;
  const from = search.get('from');
  const fromFiles = from === 'files' || (!inSet && from !== 'board');
  const stay = from === 'files' || from === 'board' ? `?from=${from}` : '';
  const staff = data.canResolve;
  return (
    <Page wide>
      {from === 'board' ? (
        <BackLink to={`/p/${projectId}/board`} label="Design board" />
      ) : fromFiles ? (
        <BackLink to={`/p/${projectId}/files`} label="Files" />
      ) : (
        <BackLink to={`/p/${projectId}/reviews`} label="Design reviews" />
      )}
      {inSet ? (
        <Title kicker={`${data.file.phase} · iteration ${data.file.iteration}`}>{data.file.setTitle}</Title>
      ) : (
        <Title kicker={[data.file.dated ? `Files · from ${day(data.file.dated)}` : 'Files', staff && !data.file.shared ? 'NSB only' : null].filter(Boolean).join(' · ')}>
          {data.file.name}
        </Title>
      )}
      {data.file.note ? <p className="-mt-3 mb-5 max-w-2xl whitespace-pre-line text-ink-2">{data.file.note}</p> : null}
      {data.file.round?.note ? (
        // The note it went to the reviewers with (design study 29), kept as it was sent.
        <div className="-mt-3 mb-5 max-w-2xl border-l-2 border-rule pl-3">
          <p className="label">{roundNotes([data.file])[0]?.label}</p>
          <p className="mt-1 whitespace-pre-line">{data.file.round.note}</p>
        </div>
      ) : null}
      {data.file.revisedBy ? (
        <p className="-mt-3 mb-5 max-w-2xl text-ink-2">
          Revised since by{' '}
          <Link to={sheetPage(projectId, data.file.revisedBy.id)} className="link text-ink">
            {data.file.revisedBy.name}
          </Link>
          . Every comment here stays.
        </p>
      ) : null}
      {data.file.sourceUrl ? (
        <p className="meta -mt-3 mb-5">
          <a href={data.file.sourceUrl} target="_blank" rel="noreferrer" className="link">
            From {sourceHost(data.file.sourceUrl)}
          </a>
        </p>
      ) : null}
      <nav aria-label={inSet ? 'Files in this iteration' : 'The project’s files'} className="-mt-3 mb-6 flex flex-wrap items-baseline gap-x-5 gap-y-1">
        {inSet ? <span className="min-w-0 truncate">{data.file.name}</span> : null}
        {siblings.length > 1 ? (
          <span className="flex items-baseline gap-4 text-sm">
            <span className="meta">
              {at + 1} of {siblings.length}
            </span>
            {prev ? (
              <Link to={`/p/${projectId}/reviews/files/${prev.id}${stay}`} className="link min-h-11 text-ink-2" aria-label={`Previous: ${prev.name}`}>
                ← Previous
              </Link>
            ) : null}
            {next ? (
              <Link to={`/p/${projectId}/reviews/files/${next.id}${stay}`} className="link min-h-11 text-ink-2" aria-label={`Next: ${next.name}`}>
                Next →
              </Link>
            ) : null}
          </span>
        ) : null}
      </nav>
      <Markup
        key={data.file.id}
        projectId={projectId}
        data={data}
        thread={search.get('thread')}
        earlier={earlier ?? null}
        onChange={(d) => {
          queryClient.setQueryData(key, d);
          void queryClient.invalidateQueries({ queryKey: reviewsKey(projectId) });
          void queryClient.invalidateQueries({ queryKey: assetsKey(projectId) });
          void queryClient.invalidateQueries({ queryKey: ['project', projectId, 'home'] });
        }}
      />
    </Page>
  );
}
